import {Component, ElementRef, OnDestroy, OnInit, ViewChild} from '@angular/core';
import {CaptureService} from '../services/capture-service';
import {AttachmentDetails} from '../model/attachment-details';
import {environment} from '../../environments/environment';
import {Subscription} from 'rxjs';
import {InvoiceService} from '../services/invoice.service';
import {ActivatedRoute, Params} from '@angular/router';
import {AuthenticationService} from '../services/authentication.service';
import * as pdfjsLib from 'pdfjs-dist';

@Component({
  selector: 'app-capture',
  templateUrl: './capture.component.html',
  styleUrls: ['./capture.component.scss'],
})
export class CaptureComponent implements OnInit, OnDestroy {
  @ViewChild('documentViewer') documentViewer: ElementRef;
  private canvas: ElementRef;
  private invoiceStoreId: any;

  @ViewChild('canvas') set content(content: ElementRef) {
    if (content) {
      // initially setter gets called with undefined
      this.canvas = content;
    }
  }

  private invoiceApiSubscription: Subscription;
  heightStyle: { height: string };
  isCanvas = false;
  isPdf = false;

  /** Variables to handle attachment canvas and pdfViewer */
  public pdfsrc: string;
  private attachment: Blob;
  public attachmentDetails: AttachmentDetails;

  totalPages = 1;
  selectedPageForOcr = 1;
  currentPdfPage = 1;
  ocrInProgress = false;
  availableOcredPages: number[] = [1];
  pdfPageNumbers: number[] = [];
  private ocrSubscription: Subscription;
  private availablePagesSubscription: Subscription;
  private ocrCompletedSubscription: Subscription;

  constructor(
    private captureService: CaptureService,
    private invoiceService: InvoiceService,
    private authenticationService: AuthenticationService,
    public route: ActivatedRoute
  ) {
  }

  ngOnInit() {
    this.route.params.subscribe((params: Params) => {
      this.captureService.getTask(params.id);
      this.getTaskInvoiceId();
    });

    this.captureService.canvasChosen.next(true);

    this.captureService.canvasChosen.subscribe((isCanvas) => {
      this.isCanvas = isCanvas;
    });

    this.captureService.isPdfSubject.subscribe((isPdf) => {
      this.isPdf = isPdf;
    });

    this.availablePagesSubscription = this.captureService.availablePages$.subscribe(
      (pages) => {
        this.availableOcredPages = this.sortPages(pages);
      }
    );

    // Handle OCR completion
    this.ocrCompletedSubscription = this.captureService.ocrCompleted$.subscribe(
      (pageNumber) => {
        setTimeout(() => {
          this.captureService.canvasChosen.next(true);

          setTimeout(() => {
            this.captureService.pageChanged.next(pageNumber.toString());
          }, 100);
        }, 500);
      }
    );
  }

  ngOnDestroy() {
    if (this.invoiceApiSubscription) {
      this.invoiceApiSubscription.unsubscribe();
    }

    if (this.ocrSubscription) {
      this.ocrSubscription.unsubscribe();
    }
    if (this.availablePagesSubscription) {
      this.availablePagesSubscription.unsubscribe();
    }
    if (this.ocrCompletedSubscription) {
      this.ocrCompletedSubscription.unsubscribe();
    }
  }

  heightChanged(e) {
    this.heightStyle = {height: `${e}px`};
  }

  onPdfLoaded(pdf: any) {
    this.totalPages = pdf.numPages;
    this.captureService.totalPdfPages$.next(this.totalPages);

    // Generate array of page numbers [1, 2, 3, ..., n]
    this.pdfPageNumbers = Array.from({length: this.totalPages}, (_, i) => i + 1);

    this.currentPdfPage = 1;
  }

  onPdfPageChange(pageNumber: number) {
    this.currentPdfPage = pageNumber;
    this.selectedPageForOcr = pageNumber;
  }

  onPageSelected(pageNumber: number) {
    if (!pageNumber) {
      return;
    }

    this.currentPdfPage = pageNumber;
  }

  isPageAlreadyOcred(pageNumber: number): boolean {
    if (pageNumber === 1) {
      return true; // Page 1 is always OCRed
    }
    if (pageNumber === this.totalPages) {
      // Check if last page has been OCRed
      return this.availableOcredPages.includes(pageNumber) ||
        this.availableOcredPages.includes(-1); // -1 represents "last_page"
    }
    return this.availableOcredPages.includes(pageNumber);
  }

  requestOcrForSelectedPage() {
    if (!this.selectedPageForOcr || this.ocrInProgress) {
      return;
    }

    // Check if already OCRed
    if (this.isPageAlreadyOcred(this.selectedPageForOcr)) {
      console.log(`Page ${this.selectedPageForOcr} is already OCRed`);
      return;
    }

    this.ocrInProgress = true;
    this.captureService.ocrProcessing$.next(true);

    this.ocrSubscription = this.invoiceService
      .requestOcrForPage(this.invoiceStoreId, this.selectedPageForOcr)
      .subscribe(
        (response) => {
          console.log('OCR completed for page', this.selectedPageForOcr);

          // Add to available pages
          const newAvailablePages = [...this.availableOcredPages, this.selectedPageForOcr];
          const sortedPages = this.sortPages(newAvailablePages);
          this.availableOcredPages = sortedPages;
          this.captureService.availablePages$.next(sortedPages);

          this.captureService.ocrCompleted$.next(this.selectedPageForOcr);

          this.ocrInProgress = false;
          this.captureService.ocrProcessing$.next(false);

          // Auto-select next non-OCRed page
          this.selectNextNonOcredPage();
        },
        (error) => {
          console.error('❌ OCR processing failed:', error);
          this.ocrInProgress = false;
          this.captureService.ocrProcessing$.next(false);
          alert(`Failed to process OCR for page ${this.selectedPageForOcr}. Please try again.`);
        }
      );
  }

  private selectNextNonOcredPage() {
    for (let i = 2; i < this.totalPages; i++) {
      if (!this.isPageAlreadyOcred(i)) {
        this.selectedPageForOcr = i;
        return;
      }
    }
    // If all middle pages are OCRed, clear selection
    this.selectedPageForOcr = null;
  }

  /**
   * Send request to API and gets invoice attachment binary.
   * @param invoiceId the invoice id.
   * @param callback callback function for retrieving request binary.
   */
  private getAttachmentBinaryFromAPI(
    invoiceId: string,
    callback = this.setAttachmentBinary.bind(this)
  ): void {
    const url = `${environment.backendUrl}/invoicestore/dataops/api/invoices/${invoiceId}/attachment-content`;
    const body = {attachmentType: 'original-file'};

    const xhr = new XMLHttpRequest();
    xhr.open('POST', url, true);
    xhr.responseType = 'blob';
    xhr.setRequestHeader('Content-Type', 'application/json');
    xhr.setRequestHeader(
      'Authorization',
      'bearer ' + this.authenticationService.getUserToken() + ''
    );

    xhr.onload = function() {
      if (this.status === 200) {
        callback(this.response);
      } else {
        console.error(xhr.statusText);
      }
    };

    xhr.send(JSON.stringify(body));
  }

  /**
   * getAttachmentDataFromAPI request callback to store attachment binary.
   * @param blob the attachment binary.
   */
  private setAttachmentBinary(blob: Blob): void {
    this.attachment = blob;
    const {mimeType} = this.attachmentDetails;

    if (this.isAttachmentImage(mimeType)) {
      this.captureService.isPdfSubject.next(false);
    } else {
      this.captureService.isPdfSubject.next(true);
      this.createPDFString(blob);

      this.getPdfPageCount(blob);
    }
  }

  private getPdfPageCount(blob: Blob): void {
    const fileReader = new FileReader();
    fileReader.onload = (e) => {
      const loadingTask = pdfjsLib.getDocument({ data: new Uint8Array(e.target.result as ArrayBuffer) });
      loadingTask.promise.then((pdf) => {
        this.totalPages = pdf.numPages;
        this.captureService.totalPdfPages$.next(pdf.numPages);
        this.pdfPageNumbers = Array.from({ length: pdf.numPages }, (_, i) => i + 1);
      });
    };
    fileReader.readAsArrayBuffer(blob);
  }

  /**
   * Check if the mimeType binary is an actual image help to determine if we use pdfViewer or the canvas.
   * @param mimeType the type of binary received from the API
   */
  private isAttachmentImage(mimeType: string): boolean {
    return (
      mimeType === 'image/png' ||
      mimeType === 'image/jpeg' ||
      mimeType === 'image/jpg'
    );
  }

  /**
   * Generate a blob url for ng2-pdf-viewer
   * @param attachment the attachment binary
   */
  private createPDFString(attachment: Blob): void {
    this.pdfsrc = URL.createObjectURL(attachment);
  }

  private getAttachmentDetailsFromApi(invoiceId: string) {
    this.invoiceApiSubscription = this.invoiceService
      .getAttachmentDetailsFromAPI(invoiceId, 'original-file')
      .subscribe((response) => {
        this.attachmentDetails = response[0];
      });
  }

  private getTaskInvoiceId() {
    this.captureService.task$.subscribe((response) => {
      this.handleResponse(response);
    });
  }

  private handleResponse(response: any) {
    this.invoiceStoreId = response.processInstanceVariables.invoice_invoiceStore_invoiceId;
    this.getAttachmentDetailsFromApi(this.invoiceStoreId);
    this.getAttachmentBinaryFromAPI(this.invoiceStoreId);

    this.initializeAvailablePages(response);
  }

  private initializeAvailablePages(response: any) {
    // Get OCR information to see which pages are available
    this.invoiceService.getOcrInformationByInvoiceId(this.invoiceStoreId)
      .subscribe((ocrData) => {
        if (ocrData && ocrData.ocrResult) {
          const parsedOcr = JSON.parse(ocrData.ocrResult);
          const pageKeys = Object.keys(parsedOcr.pageFiles || {});

          const availablePages: number[] = [];
          pageKeys.forEach(key => {
            if (key === '1') {
              availablePages.push(1);
            } else if (key === 'last_page') {
              availablePages.push(-1); // Use -1 to represent last_page
            } else {
              const pageNum = parseInt(key, 10);
              if (!isNaN(pageNum)) {
                availablePages.push(pageNum);
              }
            }
          });

          this.availableOcredPages = availablePages;
          this.captureService.availablePages$.next(availablePages);
        }
      });
  }

  private sortPages(pages: number[]): number[] {
    return [...pages].sort((a, b) => {
      // -1 represents "last_page" - putting it at the end
      if (a === -1) {
        return 1;
      }
      if (b === -1) {
        return -1;
      }
      return a - b;
    });
  }
}
